import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Badge,
  Tooltip,
  Grid
} from '@mui/material';
import {
  LibraryBooks as CatalogIcon,
  Business as DomainsIcon,
  Security as GovernanceIcon,
  VerifiedUser as ComplianceIcon,
  Analytics as AnalyticsIcon,
  Assignment as TasksIcon,
  Description as DocsIcon,
  AdminPanelSettings as AdminIcon,
  School as LearnIcon,
  AccountTree as BusinessGlossaryIcon
} from '@mui/icons-material';

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  path: string;
  description: string;
  badge?: number;
  color: string;
  shortcut?: string;
}

interface QuickNavigationPanelProps {
  items?: NavigationItem[];
}

const defaultItems: NavigationItem[] = [
  {
    id: 'catalog',
    label: 'Data Catalog',
    icon: <CatalogIcon />,
    path: '/data-catalog',
    description: 'Browse and search data assets',
    color: '#1976d2',
    shortcut: 'Ctrl+1'
  },
  {
    id: 'domains',
    label: 'Data Domains',
    icon: <DomainsIcon />,
    path: '/assets/data-domains',
    description: 'Manage data domains and ownership',
    color: '#f57c00', // Changed from #fbc02d for 508 compliance (4.53:1 contrast)
    shortcut: 'Ctrl+2'
  },
  {
    id: 'governance',
    label: 'Governance',
    icon: <GovernanceIcon />,
    path: '/governance/dashboard',
    description: 'Policies, standards, and controls',
    color: '#9c27b0',
    shortcut: 'Ctrl+3'
  },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: <ComplianceIcon />,
    path: '/compliance',
    description: 'Track compliance status and audits',
    color: '#ef6c00', // Changed from #ff9800 for 508 compliance (4.52:1 contrast)
    shortcut: 'Ctrl+4'
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: <AnalyticsIcon />,
    path: '/analytics',
    description: 'Data insights and reporting',
    color: '#00897b',
    shortcut: 'Ctrl+5'
  },
  {
    id: 'tasks',
    label: 'Task Center',
    icon: <TasksIcon />,
    path: '/tasks',
    description: 'Manage assignments and workflows',
    badge: 5,
    color: '#d32f2f', // Changed from #f44336 for 508 compliance (5.18:1 contrast)
    shortcut: 'Ctrl+6'
  },
  {
    id: 'glossary',
    label: 'Business Glossary',
    icon: <BusinessGlossaryIcon />,
    path: '/business-glossary',
    description: 'Manage business terms and definitions',
    color: '#2e7d32',
    shortcut: 'Ctrl+7'
  },
  {
    id: 'learn',
    label: 'Learn 101',
    icon: <LearnIcon />,
    path: '/learn101',
    description: 'Training and documentation',
    color: '#0288d1',
    shortcut: 'Ctrl+8'
  },
  {
    id: 'docs',
    label: 'Documentation',
    icon: <DocsIcon />,
    path: '/documentation',
    description: 'Technical documentation and guides',
    color: '#5e35b1',
    shortcut: 'Ctrl+9'
  },
  {
    id: 'admin',
    label: 'Administration',
    icon: <AdminIcon />,
    path: '/admin/users',
    description: 'System settings and user management',
    color: '#d32f2f',
    shortcut: 'Ctrl+0'
  }
];

const QuickNavigationPanel: React.FC<QuickNavigationPanelProps> = ({
  items = defaultItems
}) => {
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleKeyDown = (e: React.KeyboardEvent, path: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(path);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}
      role="navigation"
      aria-label="Quick navigation"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
          Quick Navigation
        </Typography>
        <Typography
          variant="caption"
          sx={{
            ml: 2,
            color: 'text.secondary',
            fontSize: '0.75rem'
          }}
        >
          Use keyboard shortcuts for faster access
        </Typography>
      </Box>

      <Grid container spacing={1.5}>
        {items.map((item) => (
          <Grid item xs={6} sm={4} md={2.4} key={item.id}>
            <Tooltip
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.description}
                  </Typography>
                  {item.shortcut && (
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                      Shortcut: {item.shortcut}
                    </Typography>
                  )}
                </Box>
              }
              arrow
              placement="top"
            >
              <Badge
                badgeContent={item.badge}
                color="error"
                sx={{
                  width: '100%',
                  '& .MuiBadge-badge': {
                    fontSize: '0.7rem',
                    height: 18,
                    minWidth: 18
                  }
                }}
              >
                <Chip
                  icon={React.cloneElement(item.icon, {
                    sx: { color: 'white !important', fontSize: 20 }
                  })}
                  label={item.label}
                  onClick={() => handleNavigation(item.path)}
                  onKeyDown={(e) => handleKeyDown(e, item.path)}
                  tabIndex={0}
                  sx={{
                    width: '100%',
                    height: 48,
                    bgcolor: item.color,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${item.color}40`,
                      bgcolor: item.color
                    },
                    '&:focus': {
                      outline: '3px solid #005fcc',
                      outlineOffset: '2px'
                    },
                    '& .MuiChip-label': {
                      color: 'white',
                      px: 1
                    },
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                  role="button"
                  aria-label={`Navigate to ${item.label}. ${item.description}${
                    item.badge ? `. ${item.badge} pending items` : ''
                  }`}
                />
              </Badge>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default QuickNavigationPanel;
